import { useEffect, useMemo, useState } from 'react';
import { GpsLocationDto } from '@area-restrita/api-client/model/gpsLocationDto';

import { getLastLocationUpdateStatus, haversineDistance } from '../utils';

interface CurrentLocation {
  coords: {
    latitude: number;
    longitude: number;
  };
}

export interface BusTrackingStatus {
  text: string;
  color: string;
}

export interface UseBusTrackingStatusParams {
  location: GpsLocationDto | null;
  currentLocation?: CurrentLocation | null;
}

export const useBusTrackingStatus = ({
  location,
  currentLocation,
}: UseBusTrackingStatusParams) => {
  const [lastUpdate, setLastUpdate] = useState<string>('Indisponível');
  const [isRecent, setIsRecent] = useState<boolean>(false);

  const distanceToStop = useMemo(() => {
    if (!currentLocation || !location) return;

    return haversineDistance(
      {
        lat: currentLocation.coords.latitude,
        lng: currentLocation.coords.longitude,
      },
      location,
    );
  }, [currentLocation, location]);

  useEffect(() => {
    if (!location?.dateTime) {
      setLastUpdate('Indisponível');
      setIsRecent(false);
      return;
    }

    const updateLastUpdate = () => {
      const status = getLastLocationUpdateStatus(location.dateTime);

      setLastUpdate(status.lastUpdate);
      setIsRecent(status.isRecent);
    };

    updateLastUpdate();

    const updateTimer = setInterval(updateLastUpdate, 1000);

    return () => clearInterval(updateTimer);
  }, [location]);

  const busStatus = useMemo<BusTrackingStatus>(() => {
    const speed = location?.speed;

    if (speed === null || speed === undefined) {
      return { text: 'Status desconhecido', color: 'bg-gray-500' };
    }

    if (speed === 0) return { text: 'Parado', color: 'bg-amber-500' };

    if (speed < 10) {
      return { text: 'Movendo lentamente', color: 'bg-amber-400' };
    }

    return { text: 'Em movimento', color: 'bg-green-500' };
  }, [location?.speed]);

  const estimatedArrival = useMemo(() => {
    const speedKmh = location?.speed;
    if (!speedKmh) return 'Indisponível';

    if (distanceToStop === undefined || distanceToStop === null) {
      return 'Indisponível';
    }

    const distanceKm = distanceToStop / 1000;
    const timeHours = distanceKm / speedKmh;
    const timeMinutes = Math.round(timeHours * 60);

    if (timeMinutes < 1) return 'Menos de 1 minuto';
    if (timeMinutes === 1) return '1 minuto';
    if (timeMinutes > 60) {
      const hours = Math.floor(timeMinutes / 60);
      const mins = timeMinutes % 60;
      return `${hours}h ${mins > 0 ? `${mins}min` : ''}`;
    }

    return `${timeMinutes} minutos`;
  }, [distanceToStop, location?.speed]);

  return {
    busStatus,
    distanceToStop,
    estimatedArrival,
    isRecent,
    lastUpdate,
  };
};
